import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Plug, ScrollText } from 'lucide-react';
import { api } from '../../api/client';
import type { OwnerSummary, ApiClientSummary, PaymentRequestListItem } from '@pay/shared';
import { Money } from '../../components/Money';
import { StatusBadge } from '../../components/StatusBadge';

export default function Dashboard() {
  const [owners, setOwners] = useState<OwnerSummary[]>([]);
  const [clients, setClients] = useState<ApiClientSummary[]>([]);
  const [recent, setRecent] = useState<PaymentRequestListItem[]>([]);

  useEffect(() => {
    api.get<OwnerSummary[]>('/api/admin/owners').then(setOwners).catch(() => {});
    api.get<ApiClientSummary[]>('/api/admin/clients').then(setClients).catch(() => {});
    api.get<PaymentRequestListItem[]>('/api/admin/requests?limit=10').then(setRecent).catch(() => {});
  }, []);

  return (
    <div className="p-6 max-w-6xl">
      <h1 className="text-2xl font-semibold mb-6">Dashbord</h1>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <Card icon={Building2} label="Eiere" value={owners.length} to="/admin/owners" />
        <Card icon={Plug} label="API-klienter" value={clients.length} to="/admin/clients" />
        <Card icon={ScrollText} label="Krav (siste 10)" value={recent.length} to="/admin/requests" />
      </div>

      <div className="bg-white rounded-lg shadow border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-semibold">Siste krav</h2>
          <Link to="/admin/requests" className="text-sm text-brand hover:underline">
            Se alle →
          </Link>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-6 py-3">Tidspunkt</th>
              <th className="text-left px-6 py-3">Eier</th>
              <th className="text-left px-6 py-3">Kunde</th>
              <th className="text-right px-6 py-3">Beløp</th>
              <th className="text-left px-6 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-6 py-3 text-slate-600">{new Date(r.created_at).toLocaleString('nb-NO')}</td>
                <td className="px-6 py-3">{r.owner_name}</td>
                <td className="px-6 py-3">
                  <Link to={`/admin/requests/${r.id}`} className="text-brand hover:underline">
                    {r.customer_name}
                  </Link>
                </td>
                <td className="px-6 py-3 text-right">
                  <Money ore={r.amount_ore} />
                </td>
                <td className="px-6 py-3">
                  <StatusBadge status={r.status} />
                </td>
              </tr>
            ))}
            {recent.length === 0 && (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-slate-500">
                  Ingen krav ennå.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({
  icon: Icon,
  label,
  value,
  to,
}: {
  icon: typeof Building2;
  label: string;
  value: number;
  to: string;
}) {
  return (
    <Link
      to={to}
      className="bg-white rounded-lg shadow border border-slate-200 p-5 hover:shadow-md transition"
    >
      <div className="flex items-center gap-3">
        <Icon size={28} className="text-brand" />
        <div>
          <div className="text-3xl font-semibold">{value}</div>
          <div className="text-sm text-slate-600">{label}</div>
        </div>
      </div>
    </Link>
  );
}
